import ContentTile from 'teacher-ui-client/components/tiles/content'
import { t, type IntlService } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import UiButton from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { TableContainer, TH, TRHead, Table, TD } from 'teacher-ui-client/components/reporting/table'
import type { TOC } from '@ember/component/template-only'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import type { Task } from 'ember-concurrency'
import joinList from 'teacher-ui-client/helpers/join-list'
import { formatValue } from 'teacher-ui-client/helpers/format-value'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import { or } from 'ember-truth-helpers'
import { NoDataMessage } from 'teacher-ui-client/components/no-data-message'
import { posthogEvent } from 'teacher-ui-client/modifiers/posthog'
import type { BaseReport, BaseReportMetaPaging, BaseReportMetaStandardsSet } from 'teacher-ui-client/services/reporting'
import type { LessonStandard } from 'teacher-ui-client/components/reporting/lesson-performance/lesson-selection-panel'

export interface ResultsReportData extends BaseReport {
  aggregate: {
    total_students: number
  }
  results: {
    attempts: Nullable<number>
    best_score: Nullable<number>
    first_name: string
    grade: string
    last_name: string
    teachers: string[]
  }[]
  meta: BaseReportMetaPaging & BaseReportMetaStandardsSet
}

function avgScoreColor(score: Nullable<number>): string {
  if (typeof score !== 'number') return ''
  switch (true) {
    case score <= 49:
      return 'text-watermelony-red-350'
    case score <= 79:
      return 'text-juicy-orange-350'
    default:
      return 'text-forest-green-350'
  }
}

interface Signature {
  Element: Element
  Args: {
    reportData: ResultsReportData
    selectedLessonData?: LessonStandard
    isSubco?: boolean
    isFastPhonics?: boolean
    exportCsv: Task<void, never[]>
    isReloading?: boolean
    product?: string
  }
}

export class LessonResults extends Component<Signature> {
  @service intl!: IntlService

  get lessonName(): string {
    const lesson = this.args.selectedLessonData
    if (!lesson) return ''

    if (this.args.isFastPhonics) {
      return this.intl.t('reporting.lessonPerformance.lessonSelectionNameFp', {
        lesson: lesson.lesson,
        name: lesson.name,
      })
    }
    return this.intl.t('reporting.lessonPerformance.lessonSelectionName', {
      lesson: lesson.lesson,
      name: lesson.name,
    })
  }

  get isEmptyReport(): boolean {
    return this.args.reportData.results.length === 0
  }

  <template>
    <ContentTile data-test-lesson-performance-results class="space-y-4 p-6" ...attributes>
      <LessonResultsHead
        @totalStudent={{@reportData.aggregate.total_students}}
        @lessonName={{this.lessonName}}
        @exportCsv={{@exportCsv}}
        @disabled={{this.isEmptyReport}}
        @isSubco={{@isSubco}}
        @product={{@product}}
      />
      {{#if this.isEmptyReport}}
        <NoDataMessage data-test-lesson-results-no-data @compact={{true}} />
      {{else}}
        <TableContainer>
          <Table data-test-lesson-results-table @minWidth="min-w-5xl">
            <thead>
              <TRHead data-test-results-header>
                <TH @sortKey="first_name">
                  {{t "reporting.lessonPerformance.results.table.firstName"}}
                </TH>
                <TH @sortKey="last_name">
                  {{t "reporting.lessonPerformance.results.table.lastName"}}
                </TH>
                <TH @sortKey="grade" class="w-36">
                  {{t "grade.label"}}
                </TH>
                {{#if @isSubco}}
                  <TH @sortKey="teachers">
                    {{t "reporting.lessonPerformance.results.table.teachers"}}
                  </TH>
                {{/if}}
                <TH @sortKey="attempts" class="w-36">
                  {{t "reporting.lessonPerformance.results.table.attempts"}}
                </TH>
                <TH @sortKey="best_score" class="w-40">
                  {{t "reporting.lessonPerformance.results.table.bestScore"}}
                  <span class="print:hidden">
                    <PhosphorIcon @icon="phosphor-info-fill" />
                    <Tooltip
                      @text={{if
                        @isFastPhonics
                        (t "reporting.lessonPerformance.results.table.bestScoreTooltipFp")
                        (t "reporting.lessonPerformance.results.table.bestScoreTooltip")
                      }}
                    />
                  </span>
                </TH>
              </TRHead>
            </thead>
            <tbody class="divide-y divide-neutral-50 {{if @isReloading 'opacity-50'}}">
              {{#each @reportData.results as |resultItem resultIndex|}}
                <tr data-test-results-row={{resultIndex}}>
                  <TD data-test-results-firstname>{{resultItem.first_name}}</TD>
                  <TD data-test-results-lastname>{{resultItem.last_name}}</TD>
                  <TD data-test-results-grade>{{resultItem.grade}}</TD>
                  {{#if @isSubco}}
                    <TD data-test-results-teachers>{{joinList resultItem.teachers ", "}}</TD>
                  {{/if}}
                  <TD data-test-results-attempts>{{resultItem.attempts}}</TD>
                  <TD
                    data-test-results-best-score
                    class="font-semibold"
                    @textColor={{avgScoreColor resultItem.best_score}}
                  >
                    {{formatValue resultItem.best_score append="%"}}
                  </TD>
                </tr>
              {{/each}}
            </tbody>
          </Table>
        </TableContainer>
        <BasePageSelector
          data-test-lesson-results-page-selector
          @pageCount={{@reportData.meta.total_pages}}
          @itemCount={{@reportData.meta.total_entries}}
        />
      {{/if}}
    </ContentTile>
  </template>
}

interface HeadSignature {
  Args: {
    totalStudent: number
    lessonName: string
    exportCsv: Task<unknown, unknown[]>
    disabled: boolean
    isSubco?: boolean
    product?: string
  }
}

export const LessonResultsHead: TOC<HeadSignature> = <template>
  <div class="space-y-2">
    <div class="flex">
      <h4 data-test-lesson-results-title class="grow">{{t
          "reporting.lessonPerformance.results.title"
          lessonName=@lessonName
        }}</h4>
      <span class="print:hidden">
        <PhosphorIcon @icon="phosphor-info-fill" />
        <Tooltip
          @text={{if
            @isSubco
            (t "reporting.lessonPerformance.results.legendTooltipSubco")
            (t "reporting.lessonPerformance.results.legendTooltip")
          }}
        />
      </span>
    </div>
    <div class="flex gap-2">
      <div data-test-lesson-results-student-count>{{t
          "reporting.lessonPerformance.results.showingDataForStudents"
          count=@totalStudent
        }}</div>
      <div class="ml-auto">
        <UiButton
          data-test-lesson-results-export-csv
          class="inline-flex items-center gap-1 print:hidden"
          @appearance={{ButtonAppearance.regular}}
          @disabled={{or @exportCsv.isRunning @disabled}}
          {{on "click" @exportCsv.perform}}
          {{posthogEvent "export-report-csv" report="lesson-performance" product=@product}}
        >
          <PhosphorIcon @icon="phosphor-file-x-fill" />
          {{t "reporting.lessonPerformance.results.exportCsv"}}
        </UiButton>
      </div>
    </div>
  </div>
</template>
